/**
 * SalesOrderDetailView — /sales/:id  판매 상세(백엔드 V장 "판매 상세 — 품목 고치기 · 감액" A · a · i, 2026-11-03)
 *
 * 판매 한 건 — 위에서 아래로 제목 줄(견적 번호 · 상태 · 버튼) · 품목(견적 품목 행, 목록 펼침과 같은 열 순서) · 합계 ·
 * 정보 두 칸(거래처 — 회사 칸 · 견적의 연락처 / 판매 — 담당자 · 딜 · 등록일 · 메모) · 품목 추가 · 이력(2026-11-03 "제품이 먼저").
 * 판매 등록 · 출고 요청이면 품목 줄마다 컨디션 · 수량 · 단가를 고치고(감액 = 단가 고치기) 아래 찾기로 줄을 쌓은 뒤 표 아래 [저장] 하나로
 * 한 번에 보낸다(서버 한 트랜잭션, 2026-11-03 — 행마다 [저장] 을 없앰).
 * 세팅 완료부터는 읽기만(i). 고칠 때마다 서버가 실물 묶기 · 부족분 니즈 · 합계 · 이력을 맞춘다 — 화면은 받은 상세로 다시 그린다.
 * 딜이 없으면 머리의 [딜 연결](판매 현황 줄에서 옮김 — 2026-11-03 딜 없는 판매 ④).
 * 머리 제목 줄 오른쪽에 상태 버튼 넷(출고 요청 · 세팅 완료 · 출고 · 취소) — 목록과 같은 조건(sales-order/actions). 부족이 있으면
 * 숨기지 않고 잠그고 이유 한 줄. 취소 뒤 다시 등록할 품목을 골랐으면 같은 탭에서 판매 등록으로(2026-11-03).
 * 품목 추가는 딜처럼 품목 표 바로 아래 — 찾기에서 고를 때마다 추가할 줄이 쌓이고 [추가 n건] 한 번에(서버 한 트랜잭션, 2026-11-03).
 * 다시 그리는 곳은 .sod-body(알림 · 제목 줄) · .sod-items · .sod-adding · .sod-info-region · .sod-events-region —
 * 찾기 자동완성은 그 밖(품목 카드의 고정 자리)이라 다시 그려도 새로 안 생긴다.
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import type { DirectSaleLine, ESalesOrderStatus, SalesContactView, SalesEventView, SalesOrderDetail, SalesOrderItemEditLine, SalesOrderItemView } from '../api/client';
import { AutocompleteInput } from '../components/AutocompleteInput';
import type { FreeTextItem } from '../components/AutocompleteInput';
import { openSalesCancelDialog } from '../components/sales-cancel-dialog';
import { openSalesDealLinkDialog } from '../components/sales-deal-link-dialog';
import { assert } from '../lib/assert';
import { escapeHtml, fmtDate, fmtDateTime, fmtNumber } from '../lib/cells';
import { CONDITION_OPTIONS } from '../lib/condition';
import { refreshKeepingFocus } from '../lib/keep-focus';
import { conditionOptionsOf, configChipOf, matchRowConditionText, matchRowsOf, renderStockChip } from './direct-sale/line-cells';
import type { DirectSaleFormLine, DirectSaleMatchRow } from './direct-sale/types';
import {
  CANCELLABLE, DISPATCH_CONFIRM_TEXT, DISPATCH_UNITS_CONFIRM_TEXT,
  SETUP_DONE_CONFIRM_TEXT, SHORTAGE_STATUSES, renderStepButton, reregisterHrefOf,
} from './sales-order/actions';
import { CLOSED_REASON_WORD, SALES_ORDER_STATUS_WORD, marginRateOf } from './sales-order/words';

/** 품목 줄 하나의 고칠 값 — 칸에 친 그대로(저장 전). 수량 · 단가를 비우면 NaN */
type ItemDraft = { condition: string; quantity: number; unitPriceKrw: number };

interface SalesOrderDetailData {
  id: string;
  detail: SalesOrderDetail | null;          // null = 받는 중
  events: SalesEventView[];
  drafts: Record<string, ItemDraft>;        // quote_item_id → 고치는 중인 값. 없으면 그 줄은 손 안 댐
  addings: DirectSaleFormLine[];            // 추가할 줄들(찾기에서 고른 순서). [추가 n건] 이 한 번에 보낸다
  busy: boolean;
  loadError: string;
  actionError: string;                      // 저장 · 추가 · 딜 연결 · 상태 버튼 실패(409 · 400) — 맨 위 한 줄
  notice: string;
}

const LIST_HREF = '/sales';
const PRODUCT_MATCH_LIMIT = 10;
const EDITABLE: readonly ESalesOrderStatus[] = ['preparing', 'ordered'];   // 백엔드 EDITABLE_STATUSES — 세팅 완료부터 잠금(i)
const ITEM_COLUMN_COUNT = 9;
const SAVE_CONFIRM_TEXT = '품목을 저장합니다 — 있는 실물은 묶이고 모자란 만큼 구매 요청(니즈)이 납니다.';

/** 세금계산서 발행일 칸 — 날짜 고르기 + (적혀 있으면) [비우기]. 취소(cancelled)된 판매는 글만(서버도 409) · 발행 자체는 여기서 하지 않는다 */
function renderTaxInvoice(detail: SalesOrderDetail, bActing: boolean): string {
  const dateOrNull = detail.tax_invoice_date;
  if (detail.status === 'cancelled') return dateOrNull === null ? '—' : escapeHtml(dateOrNull);
  const state = dateOrNull === null ? '<span class="sod-tax-no">미발행</span>' : '<span class="sod-tax-yes">발행</span>';
  const clear = dateOrNull === null ? '' : `<button type="button" class="btn-ghost" data-action="tax-invoice-clear" ${bActing ? 'disabled' : ''}>비우기</button>`;
  return `<span class="sod-tax">${state}<input type="date" class="pc-select" data-action="tax-invoice-date" value="${escapeHtml(dateOrNull ?? '')}" ${bActing ? 'disabled' : ''} aria-label="세금계산서 발행일">${clear}</span>`;
}

export class SalesOrderDetailView extends SolaView<SalesOrderDetailData> {
  override events = {
    click:  {
      '@save-items': 'handleSaveItems', '@drop-adding': 'handleDropAdding', '@link-deal': 'handleLinkDeal',
      '@request-dispatch': 'handleRequestDispatch', '@setup-done': 'handleSetupDone', '@dispatch': 'handleDispatch', '@cancel': 'handleCancel', '@tax-invoice-clear': 'handleTaxInvoiceClear',
    },
    change: { '@item-condition': 'handleItemCondition', '@adding-condition': 'handleAddingCondition', '@tax-invoice-date': 'handleTaxInvoiceDate' },
    input:  { '@item-quantity': 'handleItemQuantity', '@item-price': 'handleItemPrice', '@adding-quantity': 'handleAddingQuantity', '@adding-price': 'handleAddingPrice' },
  };

  /** 품목 추가의 제품 찾기 — 판매 등록과 같은 줄(제품 × 컨디션) */
  private readonly productAC: AutocompleteInput<DirectSaleMatchRow>;

  constructor(id: string) {
    super();
    this.data = { id, detail: null, events: [], drafts: {}, addings: [], busy: false, loadError: '', actionError: '', notice: '' };
    this.productAC = new AutocompleteInput<DirectSaleMatchRow>({
      placeholder: '품목 추가 — 파트넘버 · 디스크립션 · 제품번호',
      className: 'autocomplete-input-wide',
      fetchSuggestions: async (q) => matchRowsOf(await api.fetchDirectSaleProductMatches(q, PRODUCT_MATCH_LIMIT)),
      renderItem: (row) => `${configChipOf(row.product)}${escapeHtml(row.product.option_part === null ? '—' : row.product.option_part)} <span class="ac-dim">${escapeHtml(row.product.description === null ? '' : row.product.description)} · ${escapeHtml(row.product.hclt_number)} · ${escapeHtml(matchRowConditionText(row))}</span>`,
      onSelect: (item) => this.handleProductSelect(item),
    });
  }

  override getTitle(): string {
    return '판매 상세 — HCLTech';
  }

  override onMount(): void {
    void this.load();
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-sales-detail pv-page">
        <header class="page-header">
          <h1>판매 상세</h1>
          <a class="btn-ghost" data-link href="${LIST_HREF}">← 판매 현황</a>
        </header>
        <section class="page-body pv-body">
          <div class="sod-body">${this.renderBody()}</div>
          <section class="form-card pc-card ds-card sod-card sod-items-card" hidden>
            <div class="sod-items"></div>
            <div class="sod-add" hidden>
              <div class="sod-adding"></div>
              <div class="ds-product-search sod-search">${this.productAC}</div>
              <div class="sod-save"></div>
            </div>
          </section>
          <div class="sod-info-region"></div>
          <div class="sod-events-region"></div>
        </section>
      </div>`;
  }

  // ─── 불러오기 ───
  private async load(): Promise<void> {
    try {
      const [detail, events] = await Promise.all([api.fetchSalesOrder(this.data.id), api.fetchSalesOrderEvents(this.data.id)]);
      this.data = { ...this.data, detail, events, loadError: '' };
    } catch (e) {
      this.data = { ...this.data, loadError: e instanceof Error ? e.message : String(e) };
    }
    this.refreshAll();
  }

  /** 버튼 하나 — 잠그고 부른 뒤 받은 상세 · 이력으로 다시 그린다. 고치던 값은 비운다. 실패는 맨 위 한 줄. 됐으면 true */
  private async runAction(act: () => Promise<SalesOrderDetail>, noticeText: string): Promise<boolean> {
    return this.runCall(act, noticeText, true);
  }

  /** runAction 의 몸 — bClearDrafts 가 false 면 고치던 품목 값을 남긴다(세금계산서처럼 품목과 상관없는 칸) */
  private async runCall(act: () => Promise<SalesOrderDetail>, noticeText: string, bClearDrafts: boolean): Promise<boolean> {
    this.data = { ...this.data, busy: true, actionError: '', notice: '' };
    this.refreshAll();
    try {
      const detail = await act();
      const events = await api.fetchSalesOrderEvents(this.data.id);
      this.data = { ...this.data, detail, events, drafts: bClearDrafts ? {} : this.data.drafts, busy: false, notice: noticeText };
      this.refreshAll();
      return true;
    } catch (e) {
      this.data = { ...this.data, busy: false, actionError: e instanceof Error ? e.message : String(e) };
      this.refreshAll();
      return false;
    }
  }

  /** 다시 그린다 — 알림 · 제목 줄 · 품목 표 · 추가할 줄 · 정보 · 이력. 칸에 치던 포커스 · 커서는 지킨다 */
  private refreshAll(): void {
    const detail = this.data.detail;
    refreshKeepingFocus(this._rootElement ?? null, '.sod-body', () => this.refreshRegion('.sod-body', () => this.renderBody()));
    this.setHidden('.sod-items-card', detail === null);
    refreshKeepingFocus(this._rootElement ?? null, '.sod-items', () => this.refreshRegion('.sod-items', () => detail === null ? '' : this.renderItems(detail)));
    this.refreshAdding();
    this.refreshRegion('.sod-info-region', () => detail === null ? '' : this.renderInfo(detail));
    this.refreshRegion('.sod-events-region', () => detail === null ? '' : renderEvents(this.data.events));
  }

  private refreshAdding(): void {
    this.setHidden('.sod-add', !this.isEditable());
    refreshKeepingFocus(this._rootElement ?? null, '.sod-adding', () => this.refreshRegion('.sod-adding', () => this.renderAdding()));
    this.refreshSave();
  }

  /** [저장] 줄 — 고친 행 · 추가할 줄 수가 바뀔 때마다 */
  private refreshSave(): void {
    this.refreshRegion('.sod-save', () => this.renderSave());
  }

  /** [저장] — 고친 행 n · 추가 m. 둘 다 0 이면 잠김 */
  private renderSave(): string {
    const editCount = this.changedItems().length;
    const addCount = this.data.addings.length;
    const parts = [editCount > 0 ? `고침 ${fmtNumber(editCount)}` : '', addCount > 0 ? `추가 ${fmtNumber(addCount)}` : ''].filter((part) => part !== '');
    const disabled = this.data.busy || parts.length === 0 ? ' disabled' : '';
    return `<div class="sod-add-buttons"><button type="button" class="btn-primary" data-action="save-items"${disabled}>저장${parts.length === 0 ? '' : ` (${parts.join(' · ')})`}</button></div>`;
  }

  /** 손댄 품목 행 중 지금 값과 다른 것 */
  private changedItems(): SalesOrderItemView[] {
    const detail = this.data.detail;
    if (detail === null) return [];
    return detail.items.filter((item) => !isSameDraft(this.draftOf(item), itemDraftOf(item)));
  }

  private setHidden(selector: string, bHidden: boolean): void {
    const el = this._rootElement?.querySelector<HTMLElement>(selector) ?? null;
    if (el !== null) el.hidden = bHidden;
  }

  private isEditable(): boolean {
    return this.data.detail !== null && EDITABLE.includes(this.data.detail.status);
  }

  // ─── 그리기 ───
  private renderBody(): string {
    const { detail, loadError } = this.data;
    if (loadError !== '') return `<p class="text-danger">불러오지 못했습니다 — ${escapeHtml(loadError)}</p>`;
    if (detail === null) return '<p class="text-muted">불러오는 중…</p>';
    return `
      ${this.data.notice === '' ? '' : `<p class="so-notice">${escapeHtml(this.data.notice)}</p>`}
      ${this.data.actionError === '' ? '' : `<p class="so-notice so-notice-error">${escapeHtml(this.data.actionError)}</p>`}
      ${this.renderTitle(detail)}`;
  }

  /** 머리 카드 — 견적 번호 · 상태 칩(취소 사유) · 상태 버튼 넷 / 한 줄 요약(거래처 · 담당자 · 등록일) / 숫자 칸 다섯(판매 · 원가 · 마진 · 부족 · 세금계산서) */
  private renderTitle(detail: SalesOrderDetail): string {
    const closed = detail.closed_reason === null ? '' : ` <span class="sod-closed">${escapeHtml(CLOSED_REASON_WORD[detail.closed_reason])}${detail.closed_note === null ? '' : ` · ${escapeHtml(detail.closed_note)}`}</span>`;
    const meta = [detail.customer_name ?? '거래처 없음', `담당 ${detail.manager_name ?? '—'}`, `등록 ${fmtDate(detail.created_at)}`];
    return `
      <section class="form-card pc-card ds-card sod-card">
        <div class="sod-title">
          <div>
            <h2><span class="mono">${escapeHtml(detail.quote_no)}</span> <span class="so-status so-status-${detail.status}">${SALES_ORDER_STATUS_WORD[detail.status]}</span>${closed}</h2>
            <p class="sod-meta">${meta.map((part) => escapeHtml(part)).join('<span class="sod-meta-dot">·</span>')}</p>
          </div>
          ${this.renderActions(detail)}
        </div>
        <div class="sod-summary">
          ${summaryTile('판매', wonText(detail.sale_amount), '')}
          ${summaryTile('원가', wonText(detail.cost_amount), detail.cost_missing_count > 0 ? `원가 미확정 ${fmtNumber(detail.cost_missing_count)}` : '')}
          ${summaryTile('마진', marginAmountText(detail.margin_amount), detail.margin_amount === null ? '' : marginRateText(detail))}
          ${summaryTile('부족', detail.shortage_total > 0 ? `<span class="so-negative">${fmtNumber(detail.shortage_total)}</span>` : '0', detail.shortage_total > 0 ? '실물이 다 안 묶임' : '다 묶임')}
          ${summaryTile('세금계산서', detail.tax_invoice_date === null ? '<span class="sod-tax-no">미발행</span>' : '<span class="sod-tax-yes">발행</span>', detail.tax_invoice_date === null ? '' : escapeHtml(detail.tax_invoice_date))}
        </div>
      </section>`;
  }

  /** 정보 두 칸 — 거래처(회사 칸 · 견적의 연락처) · 판매(담당자 · 딜 · 등록일 · 메모 · 세금계산서 발행일) */
  private renderInfo(detail: SalesOrderDetail): string {
    const customer = detail.customer;
    return `
      <section class="form-card pc-card ds-card sod-card">
        <div class="sod-info">
          <div class="sod-info-col">
            <h3>거래처</h3>
            <dl class="sod-dl">
              <dt>이름</dt><dd>${textOrDash(detail.customer_name)}</dd>
              <dt>사업자번호</dt><dd>${textOrDash(customer === null ? null : customer.business_no)}</dd>
              <dt>대표</dt><dd>${textOrDash(customer === null ? null : customer.representative_name)}</dd>
              <dt>전화</dt><dd>${textOrDash(customer === null ? null : customer.representative_phone)}</dd>
              <dt>메일</dt><dd>${textOrDash(customer === null ? null : customer.representative_email)}</dd>
              <dt>주소</dt><dd>${textOrDash(customer === null ? null : customer.address)}</dd>
              <dt>연락처</dt><dd>${renderContact(detail.contact)}</dd>
            </dl>
          </div>
          <div class="sod-info-col">
            <h3>판매</h3>
            <dl class="sod-dl">
              <dt>담당자</dt><dd>${textOrDash(detail.manager_name)}</dd>
              <dt>딜</dt><dd>${this.renderDeal(detail)}</dd>
              <dt>등록일</dt><dd>${escapeHtml(fmtDate(detail.created_at))}</dd>
              <dt>메모</dt><dd>${textOrDash(detail.note)}</dd>
              <dt>세금계산서</dt><dd>${renderTaxInvoice(detail, this.data.busy)}</dd>
            </dl>
          </div>
        </div>
      </section>`;
  }

  /** 버튼 — 목록과 같은 다음 단계 하나(renderStepButton) + [취소]. 부족이 있으면 단계 버튼을 잠그고 그 앞에 이유 한 줄(목록은 숨긴다) */
  private renderActions(detail: SalesOrderDetail): string {
    const bCancel = CANCELLABLE.includes(detail.status);
    const busy = this.data.busy ? ' disabled' : '';
    const bShortageBlocks = detail.shortage_total > 0 && renderStepButton(detail.status, '', '') !== '';
    const moving = this.data.busy || bShortageBlocks ? ' disabled' : '';
    return `
      <div class="so-actions sod-actions">
        ${bShortageBlocks ? `<span class="sod-blocked">부족 ${fmtNumber(detail.shortage_total)} — 품목이 다 묶여야 누를 수 있습니다</span>` : ''}
        ${renderStepButton(detail.status, '', moving)}
        ${bCancel ? `<button type="button" class="btn-ghost so-danger" data-action="cancel"${busy}>취소</button>` : ''}
      </div>`;
  }

  /** 딜 — 이어진 딜 번호(새 탭) 또는 [딜 연결]. 취소된 판매는 잇지 않는다(서버도 409) — "—" */
  private renderDeal(detail: SalesOrderDetail): string {
    if (detail.deal_id !== null) {
      return `<a href="/deal/${escapeHtml(detail.deal_id)}" target="_blank" rel="noopener">${escapeHtml(detail.deal_no ?? `딜 ${detail.deal_id}`)}</a>`;
    }
    if (detail.status === 'cancelled') return textOrDash(null);
    return `<button type="button" class="btn-ghost btn-sm" data-action="link-deal"${this.data.busy ? ' disabled' : ''}>+ 딜 연결</button>`;
  }

  /** 품목 표 — 고칠 수 있으면 컨디션 · 수량 · 단가 칸과 [저장], 아니면 글. 아래 합계 */
  private renderItems(detail: SalesOrderDetail): string {
    const bEditable = this.isEditable();
    const rows = detail.items.length === 0
      ? `<tr><td colspan="${ITEM_COLUMN_COUNT}" class="ds-empty">품목이 없습니다</td></tr>`
      : detail.items.map((item) => bEditable ? this.renderEditableItem(item, detail.status) : renderReadItem(item, detail.status)).join('');
    return `
        <div class="sod-card-head">
          <h2>품목 <span class="sod-count">${fmtNumber(detail.items.length)}</span></h2>
          ${bEditable ? '<p class="sod-hint">컨디션 · 수량 · 단가를 고치고 아래 찾기로 품목을 더한 뒤 [저장] — 감액은 단가를 낮춥니다</p>' : ''}
        </div>
        <div class="sod-table-wrap">
        <table class="pv-table ds-table sod-table">
          <thead><tr><th>파트넘버</th><th>디스크립션</th><th>컨디션</th><th class="ds-num">수량</th><th class="ds-num">판매단가</th><th class="ds-num">판매단가 합계</th><th class="ds-num" title="묶인 실물의 원가 합">매입단가 합계</th><th class="ds-num">마진 금액</th><th class="ds-num">마진율</th></tr></thead>
          <tbody>${rows}</tbody>
          <tfoot><tr><td colspan="5" class="ds-num">합계</td>${renderTotalCells(detail)}</tr></tfoot>
        </table>
        </div>`;
  }

  private renderEditableItem(item: SalesOrderItemView, detailStatus: ESalesOrderStatus): string {
    const draft = this.draftOf(item);
    const id = escapeHtml(item.quote_item_id);
    return `
      <tr>
        ${renderItemNameCells(item)}
        <td class="ds-cond ds-ctrl"><select data-action="item-condition" data-id="${id}">${itemConditionOptionsOf(draft.condition)}</select></td>
        <td class="ds-num ds-ctrl"><input type="text" inputmode="numeric" class="ds-qty" data-action="item-quantity" data-id="${id}" value="${Number.isNaN(draft.quantity) ? '' : draft.quantity}"></td>
        <td class="ds-num ds-ctrl"><input type="text" inputmode="numeric" class="ds-price" data-action="item-price" data-id="${id}" value="${Number.isNaN(draft.unitPriceKrw) ? '' : draft.unitPriceKrw}"></td>
        ${renderAmountCells(item, detailStatus)}
      </tr>`;
  }

  /** 추가할 줄들 — 고른 제품 · 컨디션(재고 칩) · 수량 · 단가 · [빼기]. 아래 [추가 n건]. 없으면 안내 한 줄 */
  private renderAdding(): string {
    const { addings } = this.data;
    if (addings.length === 0) return '<p class="text-muted sod-hint sod-add-hint">아래에서 제품을 찾아 고르면 추가할 줄이 쌓입니다</p>';
    const rows = addings.map((line, index) => `
        <tr>
          <td class="mono">${escapeHtml(line.product.option_part === null ? '—' : line.product.option_part)}</td>
          <td class="ds-desc">${configChipOf(line.product)}${escapeHtml(line.product.description === null ? '' : line.product.description)}</td>
          <td class="ds-cond ds-ctrl"><select data-action="adding-condition" data-index="${index}"><option value="">고르기</option>${conditionOptionsOf(line)}</select>${renderStockChip(line)}</td>
          <td class="ds-num ds-ctrl"><input type="text" inputmode="numeric" class="ds-qty" data-action="adding-quantity" data-index="${index}" value="${Number.isNaN(line.quantity) ? '' : line.quantity}"></td>
          <td class="ds-num ds-ctrl"><input type="text" inputmode="numeric" class="ds-price" data-action="adding-price" data-index="${index}" value="${line.unitPriceKrw === null ? '' : line.unitPriceKrw}" placeholder="원"></td>
          <td class="ds-ctrl"><button type="button" class="btn-icon ds-remove" data-action="drop-adding" data-index="${index}" title="빼기" aria-label="빼기">✕</button></td>
        </tr>`).join('');
    return `
      <div class="sod-table-wrap">
      <table class="pv-table ds-table sod-adding-table">
        <thead><tr><th>추가할 품목</th><th>디스크립션</th><th>컨디션</th><th class="ds-num">수량</th><th class="ds-num">단가</th><th></th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      </div>
`;
  }

  /** 그 줄의 고치는 값 — 손 안 댔으면 지금 값 */
  private draftOf(item: SalesOrderItemView): ItemDraft {
    const draft = this.data.drafts[item.quote_item_id];
    return draft === undefined ? itemDraftOf(item) : draft;
  }

  private updateDraft(el: HTMLElement, patch: Partial<ItemDraft>): void {
    const item = this.itemOf(el);
    this.data = { ...this.data, drafts: { ...this.data.drafts, [item.quote_item_id]: { ...this.draftOf(item), ...patch } } };
    this.refreshSave();
  }

  private itemOf(el: HTMLElement): SalesOrderItemView {
    const id = el.dataset.id;
    const item = this.data.detail === null ? undefined : this.data.detail.items.find((row) => row.quote_item_id === id);
    assert(item !== undefined, `품목 칸의 data-id '${String(id)}' 가 상세의 품목 행에 없다 — renderEditableItem 이 붙인 id 만 온다`);
    return item;
  }

  // ─── 핸들러 ───
  handleItemCondition(_e: Event, el: HTMLElement): void {
    this.updateDraft(el, { condition: (el as HTMLSelectElement).value });
  }

  handleItemQuantity(_e: Event, el: HTMLElement): void {
    this.updateDraft(el, { quantity: numberOfText((el as HTMLInputElement).value) });
  }

  handleItemPrice(_e: Event, el: HTMLElement): void {
    this.updateDraft(el, { unitPriceKrw: numberOfText((el as HTMLInputElement).value) });
  }

  private handleProductSelect(item: DirectSaleMatchRow | FreeTextItem): void {
    assert(!('__new' in item), '제품 자동완성은 allowFreeText 를 안 켰다 — 새 제품 줄이 올 수 없다');
    this.data = { ...this.data, addings: [...this.data.addings, { product: item.product, condition: item.condition, quantity: 1, unitPriceKrw: null }] };
    this.productAC.setValue('');
    this.refreshAdding();
  }

  handleAddingCondition(_e: Event, el: HTMLElement): void {
    this.updateAdding(el, { condition: (el as HTMLSelectElement).value });
  }

  handleAddingQuantity(_e: Event, el: HTMLElement): void {
    this.updateAdding(el, { quantity: numberOfText((el as HTMLInputElement).value) });
  }

  handleAddingPrice(_e: Event, el: HTMLElement): void {
    const value = numberOfText((el as HTMLInputElement).value);
    this.updateAdding(el, { unitPriceKrw: Number.isNaN(value) ? null : value });
  }

  handleDropAdding(_e: Event, el: HTMLElement): void {
    const index = addingIndexOf(el, this.data.addings);
    this.data = { ...this.data, addings: this.data.addings.filter((_line, at) => at !== index) };
    this.refreshAdding();
  }

  /** [저장] — 고친 행 · 추가할 줄을 한 번에. 값이 틀리면 맨 위 한 줄("고침 P00924-B21 — …" · "추가 2번째 줄 — …"). 되면 둘 다 비운다 */
  async handleSaveItems(): Promise<void> {
    const changed = this.changedItems().map((item) => ({ item, draft: this.draftOf(item) }));
    const added = this.data.addings.map((line) => ({ line, draft: addingDraftOf(line) }));
    assert(changed.length + added.length > 0, '[저장] 은 고친 행이나 추가할 줄이 있을 때만 열린다 — renderSave');
    const errors = [
      ...changed.map(({ item, draft }) => errorTextOrNull(`고침 ${itemLabelOf(item)}`, draft)),
      ...added.map(({ draft }, index) => errorTextOrNull(`추가 ${index + 1}번째 줄`, draft)),
    ].filter((text): text is string => text !== null);
    if (errors.length > 0) {
      this.data = { ...this.data, actionError: errors.join(' · '), notice: '' };
      this.refreshAll();
      return;
    }
    if (!confirm(SAVE_CONFIRM_TEXT)) return;
    const edits: SalesOrderItemEditLine[] = changed.map(({ item, draft }) => ({ quote_item_id: item.quote_item_id, condition: draft.condition, quantity: draft.quantity, unit_price_krw: draft.unitPriceKrw }));
    const lines: DirectSaleLine[] = added.map(({ line, draft }) => ({ hclt_product_id: Number(line.product.hclt_product_id), condition: draft.condition, quantity: draft.quantity, unit_price_krw: draft.unitPriceKrw }));
    const bDone = await this.runAction(() => api.saveSalesOrderItems(this.data.id, edits, lines), '저장했습니다');
    if (bDone) {
      this.data = { ...this.data, addings: [] };
      this.refreshAdding();
    }
  }

  /** 세금계산서 발행일 — 날짜를 고르면 바로 저장(같은 날이면 서버가 아무것도 안 한다) */
  async handleTaxInvoiceDate(_e: Event, el: HTMLElement): Promise<void> {
    const value = (el as HTMLInputElement).value;
    if (value === '') return;   // 칸을 지우는 것은 [비우기] 로 — 반쯤 친 날짜에서 저장하지 않는다
    await this.runCall(() => api.updateSalesOrderTaxInvoice(this.data.id, value), '세금계산서 발행일을 적었습니다', false);
  }

  /** [비우기] — 세금계산서 발행일을 지운다(아직 발행 안 함으로) */
  async handleTaxInvoiceClear(): Promise<void> {
    if (!confirm('세금계산서 발행일을 지웁니다 — 아직 발행 안 함으로 돌아갑니다.')) return;
    await this.runCall(() => api.updateSalesOrderTaxInvoice(this.data.id, null), '세금계산서 발행일을 지웠습니다', false);
  }

  /** [딜 연결] — 그 거래처의 딜을 골라 잇는다 */
  async handleLinkDeal(): Promise<void> {
    const detail = this.data.detail;
    assert(detail !== null, '[딜 연결] 은 상세를 받은 뒤에만 그린다 — renderDeal');
    const dealId = await openSalesDealLinkDialog(detail);
    if (dealId === null) return;
    await this.runAction(() => api.linkSalesOrderDeal(this.data.id, dealId), '딜에 연결했습니다');
  }

  async handleRequestDispatch(): Promise<void> {
    if (!confirm(DISPATCH_CONFIRM_TEXT)) return;
    await this.runAction(() => api.requestSalesOrderDispatch(this.data.id), '출고 요청을 보냈습니다');
  }

  async handleSetupDone(): Promise<void> {
    if (!confirm(SETUP_DONE_CONFIRM_TEXT)) return;
    await this.runAction(() => api.completeSalesOrderSetup(this.data.id), '세팅 완료로 옮겼습니다');
  }

  async handleDispatch(): Promise<void> {
    if (!confirm(DISPATCH_UNITS_CONFIRM_TEXT)) return;
    await this.runAction(() => api.dispatchSalesOrder(this.data.id), '출고했습니다');
  }

  /** [취소] — 취소 창(사유 · 다시 등록할 품목). 다시 등록할 품목이 있으면 취소 뒤 같은 탭에서 판매 등록으로 */
  async handleCancel(): Promise<void> {
    const detail = this.data.detail;
    assert(detail !== null, '[취소] 는 상세를 받은 뒤에만 그린다 — renderActions');
    const choice = await openSalesCancelDialog(detail);
    if (choice === null) return;
    const bDone = await this.runAction(() => api.cancelSalesOrder(this.data.id, choice.input), '취소했습니다');
    if (bDone && choice.reregister_item_ids.length > 0) {
      this.navigateTo(reregisterHrefOf(this.data.id, choice.reregister_item_ids));
    }
  }

  private navigateTo(path: string): void {
    // SolaRouter 의 a[data-link] 인터셉트와 동일 동작 — history.pushState
    history.pushState(null, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  /** 추가할 줄의 칸 하나 — 재고 칩이 바뀌어 추가 칸을 다시 그린다 */
  private updateAdding(el: HTMLElement, patch: Partial<DirectSaleFormLine>): void {
    const index = addingIndexOf(el, this.data.addings);
    this.data = { ...this.data, addings: this.data.addings.map((line, at) => at === index ? { ...line, ...patch } : line) };
    this.refreshAdding();
  }
}

// ─── 그리기 — 줄 하나 ───
function renderReadItem(item: SalesOrderItemView, detailStatus: ESalesOrderStatus): string {
  return `
    <tr>
      ${renderItemNameCells(item)}
      <td>${textOrDash(item.condition)}</td>
      <td class="ds-num">${fmtNumber(item.quantity)}</td>
      <td class="ds-num">${wonText(item.unit_price_krw)}</td>
      ${renderAmountCells(item, detailStatus)}
    </tr>`;
}

/** 파트넘버 · 디스크립션 */
function renderItemNameCells(item: SalesOrderItemView): string {
  return `
      <td class="mono">${escapeHtml(item.option_part ?? '—')}</td>
      <td class="ds-desc">${item.is_config ? `<span class="chip">${item.product_type === 'set' ? '세트' : '서버 구성'}</span> ` : ''}${escapeHtml(item.description ?? '')}</td>`;
}

/** 판매단가 합계 · 매입단가 합계 · 마진 금액 · 마진율 — 견적 표와 같은 말 · 순서(2026-11-03).
 *  진행 중인 판매에서 실물이 모자라면 마진 금액 칸에 "구매 필요 n"(원가가 없어 마진이 안 나온다) */
function renderAmountCells(item: SalesOrderItemView, status: ESalesOrderStatus): string {
  const bNeedPurchase = SHORTAGE_STATUSES.includes(status) && item.shortage > 0;
  const margin = bNeedPurchase ? `<span class="so-short">구매 필요 ${fmtNumber(item.shortage)}</span>` : marginAmountText(item.margin_amount);
  return `
      <td class="ds-num">${wonText(item.sale_amount)}</td>
      <td class="ds-num">${wonText(item.cost_amount)}</td>
      <td class="ds-num">${margin}</td>
      <td class="ds-num">${bNeedPurchase ? '—' : marginRateText(item)}</td>`;
}

/** 합계 줄 — 판매단가 합계 · 매입단가 합계 · 마진 금액 · 마진율. 원가가 다 안 나왔으면 "확인 필요"(견적 합계와 같은 말) */
function renderTotalCells(detail: SalesOrderDetail): string {
  const bCostKnown = detail.cost_amount !== null;
  return `
      <td class="ds-num"><strong>${wonText(detail.sale_amount)}</strong></td>
      <td class="ds-num">${bCostKnown ? wonText(detail.cost_amount) : '확인 필요'}</td>
      <td class="ds-num">${bCostKnown ? marginAmountText(detail.margin_amount) : '확인 필요'}</td>
      <td class="ds-num">${bCostKnown ? marginRateText(detail) : '확인 필요'}</td>`;
}

/** 연락처 한 칸 — 이름 직책 · 휴대폰 · 사무실 · 메일(있는 것만). 없으면 — */
function renderContact(contact: SalesContactView | null): string {
  if (contact === null) return textOrDash(null);
  const name = contact.position === null ? contact.name : `${contact.name} ${contact.position}`;
  const parts = [name, contact.phone, contact.office_phone, contact.email].filter((part): part is string => part !== null && part !== '');
  return escapeHtml(parts.join(' · '));
}

/** 머리 카드의 숫자 칸 하나 — 이름 · 값(그린 글) · 아래 작은 글('' 이면 없음) */
function summaryTile(label: string, valueHtml: string, subHtml: string): string {
  return `<div class="sod-sum-tile"><span class="sod-sum-label">${label}</span><span class="sod-sum-value">${valueHtml}</span>${subHtml === '' ? '' : `<span class="sod-sum-sub">${subHtml}</span>`}</div>`;
}

/** 이력 — 최신순 한 줄씩(날짜 · 글 · 누른 사람) */
function renderEvents(events: readonly SalesEventView[]): string {
  const rows = events.length === 0
    ? '<li class="text-muted">아직 이력이 없습니다 — 상태 이동 · 취소 · 딜 연결 · 품목 고치기 · 추가가 여기에 남습니다</li>'
    : events.map((event) => `<li><span class="sod-event-at">${escapeHtml(fmtDateTime(event.created_at))}</span><span class="sod-event-text">${escapeHtml(event.message)}</span><span class="sod-event-who">${event.actor_name === null ? '' : escapeHtml(event.actor_name)}</span></li>`).join('');
  return `
      <section class="form-card pc-card ds-card sod-card">
        <div class="sod-card-head"><h2>이력 <span class="sod-count">${fmtNumber(events.length)}</span></h2></div>
        <ul class="sod-events">${rows}</ul>
      </section>`;
}

/** 품목 줄의 컨디션 고르기 — CONDITION_OPTIONS + 지금 값(목록 밖이면) */
function itemConditionOptionsOf(current: string): string {
  const known: readonly string[] = CONDITION_OPTIONS;
  const conditions = known.includes(current) || current === '' ? known : [...known, current];
  return conditions.map((condition) => `<option value="${escapeHtml(condition)}"${condition === current ? ' selected' : ''}>${escapeHtml(condition)}</option>`).join('');
}

// ─── 순수 함수 ───
/** 값 검사 글 — 틀리면 "<자리> — <이유>", 맞으면 null */
function errorTextOrNull(place: string, draft: ItemDraft): string | null {
  const errorOrNull = draftErrorOrNull(draft);
  return errorOrNull === null ? null : `${place} — ${errorOrNull}`;
}

/** 고칠 값 둘이 같은가 — 손댔다 되돌린 행은 고친 행이 아니다 */
function isSameDraft(left: ItemDraft, right: ItemDraft): boolean {
  return left.condition === right.condition && Object.is(left.quantity, right.quantity) && Object.is(left.unitPriceKrw, right.unitPriceKrw);
}

/** 추가할 줄 → 고칠 값 꼴(같은 검사를 쓰려고). 빈 단가는 NaN — draftErrorOrNull 이 막는다 */
function addingDraftOf(line: DirectSaleFormLine): ItemDraft {
  return { condition: line.condition, quantity: line.quantity, unitPriceKrw: line.unitPriceKrw === null ? Number.NaN : line.unitPriceKrw };
}

/** 추가할 줄 칸의 data-index → 줄 번호. renderAdding 이 붙인 번호만 온다 */
function addingIndexOf(el: HTMLElement, addings: readonly DirectSaleFormLine[]): number {
  const index = Number(el.dataset.index);
  assert(Number.isInteger(index) && index >= 0 && index < addings.length, `추가할 줄 칸의 data-index '${String(el.dataset.index)}' 가 줄 ${addings.length} 개 밖이다 — renderAdding 이 붙인 번호만 온다`);
  return index;
}

/** 품목 행의 지금 값 → 고칠 값. 컨디션 · 단가가 비었으면 '' · 0 */
function itemDraftOf(item: SalesOrderItemView): ItemDraft {
  return { condition: item.condition ?? '', quantity: item.quantity, unitPriceKrw: item.unit_price_krw ?? 0 };
}

/** 고칠 값의 틀린 곳 — 백엔드 검사와 같은 셋(수량 1 이상 정수 · 단가 0 이상 · 컨디션). 맞으면 null */
function draftErrorOrNull(draft: ItemDraft): string | null {
  if (draft.condition === '') return '컨디션을 골라 주세요';
  if (!Number.isInteger(draft.quantity) || draft.quantity < 1) return '수량은 1 이상 정수입니다';
  if (!Number.isFinite(draft.unitPriceKrw) || draft.unitPriceKrw < 0) return '단가를 0 이상으로 넣어 주세요';
  return null;
}

/** 칸 글 → 수 — 쉼표 · 공백은 뗀다. 비우면 NaN */
function numberOfText(text: string): number {
  const cleaned = text.replaceAll(',', '').trim();
  return cleaned === '' ? Number.NaN : Number(cleaned);
}

function itemLabelOf(item: SalesOrderItemView): string {
  return item.option_part ?? item.hclt_number;
}

function wonText(amount: number | null): string {
  return amount === null ? '—' : fmtNumber(Math.round(amount));
}

function textOrDash(text: string | null): string {
  return text === null ? '<span class="text-muted">—</span>' : escapeHtml(text);
}

/** 마진 금액 — 음수는 붉게(판매 현황과 같은 꼴) */
function marginAmountText(amount: number | null): string {
  const negative = amount !== null && amount < 0 ? ' class="so-negative"' : '';
  return `<span${negative}>${wonText(amount)}</span>`;
}

/** 마진율 — 원가가 없으면 — */
function marginRateText(amounts: { cost_amount: number | null; margin_amount: number | null }): string {
  const rate = marginRateOf(amounts);
  if (rate === null) return '—';
  const negative = rate < 0 ? ' class="so-negative"' : '';
  return `<span${negative}>${rate.toFixed(0)}%</span>`;
}
